import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Resolve, ActivatedRouteSnapshot } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { TransferState, makeStateKey } from '@angular/platform-browser';
import { isPlatformServer } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class PricingResolver implements Resolve<any> {
  constructor(
    private http: HttpClient,
    private transferState: TransferState,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  private useTransferState<T>(key: string, observable$: Observable<T>): Observable<T> {
    const STATE_KEY = makeStateKey<T>(key);

    if (this.transferState.hasKey(STATE_KEY)) {
      return of(this.transferState.get<T>(STATE_KEY, null as any));
    }

    return observable$.pipe(
      map(data => {
        if (isPlatformServer(this.platformId)) {
          this.transferState.set(STATE_KEY, data);
        }
        return data;
      })
    );
  }

  resolve(route: ActivatedRouteSnapshot): Observable<any> {
    const lang = route.url[0]?.path || 'en'; // ✅ Fallback to 'en' for safety

    // ✅ Use absolute paths for SSR compatibility
    const pricingUrl = `/assets/${environment.projectName}/content/${lang}/price-page/pricing.json`;
    const metadataUrl = `/assets/${environment.projectName}/content/${lang}/price-page/meta-data.json`;

    return forkJoin({
      data: this.useTransferState(
        `pricingData-${lang}`,
        this.http.get<any>(pricingUrl).pipe(catchError(() => of(null)))
      ),
      meta: this.useTransferState(
        `pricingMeta-${lang}`,
        this.http.get<any>(metadataUrl).pipe(catchError(() => of(null)))
      )
    }).pipe(
      map(({ data, meta }) => ({ data, meta })),
      catchError(error => {
        console.error('Error fetching pricing or metadata:', error);
        return of(null);
      })
    );
  }
}
